/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { useStandardExperienceModeQuery } from '~sq-server-commons/queries/mode';
import { Component } from '~sq-server-commons/types/types';
import { useSandboxSettingsMutation, useSandboxSettingsQuery } from '../../queries';
import { SandboxInstanceSettings, SandboxSettingValidationState } from '../../types';
import { useSandboxSettingForm } from '../hooks/useSandboxSettingForm';
import SandboxSettingDefaultEnablementSection from './SandboxSettingDefaultEnablementSection';
import SandboxSettingQualitiesOverrideSection from './SandboxSettingQualitiesOverrideSection';
import SandboxSettingQualitiesSection from './SandboxSettingQualitiesSection';
import SandboxSettingToggleSection from './SandboxSettingToggleSection';
import { SandboxSettingWrapper } from './SandboxSettingWrapper';

export function SandboxInstanceSetting(_: Readonly<{ component?: Component }>) {
  const { data: isStandardExperience } = useStandardExperienceModeQuery();
  const { data: sandboxInstanceSetting, isLoading: loadingInstanceSetting } =
    useSandboxSettingsQuery();
  const { mutate: updateSandboxSettings, isPending: isUpdating } = useSandboxSettingsMutation();
  const {
    formState,
    validationState,
    setEnabled,
    setSoftwareQualities,
    setTypes,
    setAllowOverride,
    isChanged,
    hasAddedCriticalTypesFlag,
    setDefaultValue,
    reset,
    validate,
  } = useSandboxSettingForm(sandboxInstanceSetting);

  const doSubmit = () => {
    const body: Partial<SandboxInstanceSettings> = formState.enabled
      ? {
          enabled: formState.enabled,
          defaultValue: Boolean(formState.defaultValue),
          allowOverride: Boolean(formState.allowOverride),
          ...(isStandardExperience
            ? {
                types: formState.types,
              }
            : {
                softwareQualities: formState.softwareQualities,
              }),
        }
      : { enabled: formState.enabled };
    updateSandboxSettings(body);
  };

  return (
    <SandboxSettingWrapper
      doSubmit={doSubmit}
      hasAddedCriticalTypesFlag={hasAddedCriticalTypesFlag}
      isChanged={isChanged}
      isLoading={loadingInstanceSetting}
      isUpdating={isUpdating}
      onReset={reset}
      onSubmit={validate}
    >
      <SandboxSettingToggleSection isEnabled={formState.enabled} onChange={setEnabled} />
      {formState.enabled && (
        <div className="sw-ml-7">
          <SandboxSettingQualitiesSection
            hasAddedCriticalTypes={hasAddedCriticalTypesFlag}
            isInvalid={
              validationState.softwareQualities === SandboxSettingValidationState.Invalid ||
              validationState.types === SandboxSettingValidationState.Invalid
            }
            level="instance"
            onQualitiesChange={setSoftwareQualities}
            onTypesChange={setTypes}
            qualities={formState.softwareQualities}
            types={formState.types}
          />
          <SandboxSettingQualitiesOverrideSection
            allowOverride={formState.allowOverride}
            onOverrideChange={setAllowOverride}
          />
          <SandboxSettingDefaultEnablementSection
            defaultValue={formState.defaultValue}
            isInvalid={validationState.defaultValue === SandboxSettingValidationState.Invalid}
            onDefaultEnablementChange={setDefaultValue}
          />
        </div>
      )}
    </SandboxSettingWrapper>
  );
}
